import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Close from "@mui/icons-material/Close";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Refresh from "@mui/icons-material/Refresh";
import Replay from "@mui/icons-material/Replay";
import Search from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import { colors } from "healthex-common/colors";
import { observer } from "mobx-react";
import { NOT_LOADED } from "../Infra/RemoteData";
import { addDisplayName } from "../Infra/display-name";
import { ChipNeutral, MonoText } from "./AdminWebhooksListView.styles";
import { LoadingSpinner } from "./DocumentRetrievalReviewAndShare.styles";
import { HXButton } from "./HXButton";
import { HXDataGrid } from "./HXDataGrid";
import { AttemptCell, ChevronCell, DeliveryLogParent, FollowButton, Inspector, InspectorActions, InspectorClose, InspectorEventId, InspectorEventIdRow, InspectorHeader, InspectorLabel, InspectorPayload, InspectorSection, InspectorTitle, LoadingSpinnerContainer, LogFilterBar, LogFilterChip, LogLayout, LogListPane, LogRefreshButton, LogSearchField, ResponseMetaMuted, ResponseMetaRow, RetentionNote, StatusCode, } from "./WebhookDeliveryLogView.styles";
import { WebhookDeliveryLogViewModel } from "./WebhookDeliveryLogViewModel";
import { FlexColumn } from "./common.styles";
import { useViewModel } from "./useViewModel";
import React from "react";
const FILTERS = [
    { value: "ALL", label: "All" },
    { value: "DELIVERED", label: "Delivered" },
    { value: "FAILED", label: "Failed" },
    { value: "RETRIED", label: "Retried" },
];
export const WebhookDeliveryLogView = observer((props) => {
    const model = useViewModel(WebhookDeliveryLogViewModel, props);
    React.useLayoutEffect(() => {
        model.updateWebhook(props.webhook);
    }, [props.webhook]);
    const inspector = model.selectedInspector;
    const withInspector = inspector !== null;
    return (_jsxs(DeliveryLogParent, { children: [_jsx(RetentionNote, { children: "Delivery attempts are retained for 30 days." }), _jsxs(LogLayout, { "$withInspector": withInspector, children: [_jsxs(LogListPane, { "$withInspector": withInspector, children: [_jsxs(LogFilterBar, { children: [FILTERS.map((option) => (_jsx(LogFilterChip, { "$active": model.filter === option.value, onClick: () => model.setFilter(option.value), children: option.label }, option.value))), _jsx(LogSearchField, { placeholder: "Search message ID or event type", value: model.searchText, onChange: (event) => model.setSearchText(event.target.value), InputProps: {
                                            startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(Search, { fontSize: "small" }) })),
                                        } }), _jsx(Tooltip, { title: "Refresh", children: _jsx(LogRefreshButton, { "aria-label": "Refresh delivery log", onClick: () => model.refresh(), children: _jsx(Refresh, { fontSize: "small" }) }) })] }), model.attempts === NOT_LOADED ? (_jsx(LoadingSpinnerContainer, { children: _jsx(LoadingSpinner, {}) })) : (_jsx(HXDataGrid, { rows: model.attempts, columns: columns(), getRowId: (attempt) => attempt.id, sortingMode: "server", sortModel: [{ field: "timestampLabel", sort: "desc" }], onSortModelChange: () => { }, rowCount: model.total, paginationModel: model.paginationInput, onPaginationModelChange: (paginationModel) => model.updatePagination(paginationModel), paginationSelectedLabel: "delivery attempt", onRowClick: (params) => model.selectAttempt(params.row), getRowClassName: (params) => { var _a; return (params.id === ((_a = model.selectedInspector) === null || _a === void 0 ? void 0 : _a.id) ? "attempt-selected" : ""); }, sx: {
                                    "& .MuiDataGrid-row": { cursor: "pointer" },
                                    "& .attempt-selected, & .attempt-selected:hover": {
                                        backgroundColor: colors.blue_10,
                                        boxShadow: `inset 3px 0 0 ${colors.blue_main}`,
                                    },
                                }, localeText: { noRowsLabel: model.noRowsMessage } }))] }), inspector && (_jsxs(Inspector, { children: [_jsxs(InspectorHeader, { children: [_jsxs("div", { children: [_jsx(InspectorTitle, { children: "Delivery inspector" }), _jsxs(InspectorEventIdRow, { children: [_jsx(InspectorEventId, { children: inspector.messageId }), _jsx(FollowButton, { onClick: () => model.followMessage(), children: "Follow" })] })] }), _jsx(InspectorClose, { "aria-label": "Close inspector", onClick: () => model.closeInspector(), children: _jsx(Close, { fontSize: "small" }) })] }), _jsx(InspectorSection, { children: _jsxs(FlexColumn, { gap: 16, children: [_jsxs("div", { children: [_jsx(InspectorLabel, { children: "Headers sent" }), _jsx(InspectorPayload, { children: inspector.eventHeadersFormatted })] }), _jsxs("div", { children: [_jsx(InspectorLabel, { children: "Payload sent" }), _jsx(InspectorPayload, { children: inspector.eventDataFormatted })] })] }) }), _jsxs(InspectorSection, { children: [_jsx(InspectorLabel, { children: "Response received" }), _jsxs(ResponseMetaRow, { children: [_jsx(StatusCode, { outcome: inspector.outcome, children: inspector.statusLabel }), inspector.responseLatencyMs !== null && (_jsxs(ResponseMetaMuted, { children: [inspector.responseLatencyMs, " ms"] })), inspector.responseSizeBytes !== null && (_jsxs(ResponseMetaMuted, { children: [inspector.responseSizeBytes, " bytes"] }))] }), inspector.responseBody && _jsx(InspectorPayload, { children: inspector.responseBody })] }), _jsxs(InspectorActions, { children: [_jsx(Tooltip, { title: model.replayEnabled ? null : "Webhook is paused. Resume to replay deliveries.", children: _jsx("div", { children: _jsx(HXButton, { secondary: true, disabled: !model.replayEnabled, startIcon: _jsx(Replay, {}), onClick: () => model.replayDelivery(), children: "Replay delivery" }) }) }), _jsx(HXButton, { secondary: true, startIcon: _jsx(ContentCopy, {}), onClick: () => void model.copyMessageId(), children: "Copy message ID" })] })] }))] })] }));
    function columns() {
        return [
            { field: "timestampLabel", headerName: "Timestamp", flex: 0.9 },
            {
                field: "messageId",
                headerName: "Message ID",
                flex: 1.2,
                sortable: false,
                cellClassName: "singleLineEllipis",
                renderCell: (params) => {
                    if (params.value === undefined)
                        return null;
                    return _jsx(MonoText, { children: params.value });
                },
            },
            {
                field: "event",
                headerName: "Event",
                flex: 1,
                sortable: false,
                renderCell: (params) => {
                    if (params.value === undefined)
                        return null;
                    return _jsx(MonoText, { children: params.value });
                },
            },
            {
                field: "project",
                headerName: "Project",
                flex: 0.9,
                sortable: false,
                renderCell: (params) => {
                    const project = params.row.projectName;
                    return project ? _jsx(ChipNeutral, { label: project }) : _jsx(MonoText, { children: "\u2014" });
                },
            },
            {
                field: "status",
                headerName: "Status",
                flex: 0.6,
                sortable: false,
                renderCell: (params) => (_jsx(StatusCode, { outcome: params.row.outcome, children: params.row.statusLabel })),
            },
            {
                field: "attempt",
                headerName: "Attempt",
                flex: 0.6,
                sortable: false,
                renderCell: (params) => {
                    const { attempt, maxAttempts, outcome } = params.row;
                    return (_jsxs(AttemptCell, { "$terminal": outcome === "FAILED" && attempt >= maxAttempts, children: [attempt, " of ", maxAttempts] }));
                },
            },
            {
                field: "chevron",
                headerName: "",
                flex: 0.2,
                sortable: false,
                renderCell: () => (_jsx(ChevronCell, { children: _jsx(ChevronRight, { fontSize: "small" }) })),
            },
        ];
    }
});
addDisplayName(WebhookDeliveryLogView, "WebhookDeliveryLogView");
